import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Box,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  OutlinedInput,
  Chip,
  Tabs,
  Tab,
  Typography,
  Container,
  Autocomplete,
  TextField,
  TablePagination,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { ITableProps } from "./CropCycleStatusWiseTableTypes";
import { useNavigate } from "react-router-dom";
import CommonDrawer from "../../../../components/shared/commonDrawer/CommonDrawer";
import { getApi } from "../../../../services/apiManager/ApiManager";

interface ILocalFilterData {
  status?: string;
  statusLabel?: string;
}

interface StatusFilter {
  value: string;
  label: string;
}

const CommonTable: React.FC<ITableProps> = ({
  tableName,
  columns,
  buttonConfig,
  formatData,
  rowClick,
  isHideImportExportAddBtns,
  isFilterBtn,
  isResetSearch,
  fieldId,
}) => {
  const { t } = useTranslation();
  const [data, setData] = useState<any[]>([]);
  const [search, setSearch] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"ACTIVE" | "PAST">("ACTIVE");
  const [statusFilter, setStatusFilter] = useState<StatusFilter | null>(null);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filterInitialValues, setFilterInitialValues] =
    useState<ILocalFilterData>({});
  const [page, setPage] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [pageSize] = useState(10);
  const [activeTotal, setActiveTotal] = useState(0);
  const [pastTotal, setPastTotal] = useState(0);
  const [countsLoaded, setCountsLoaded] = useState(false);
  const navigate = useNavigate();
  const rowsPerPage = 10;
  const formatDataRef = useRef(formatData);

  // Update ref when formatData changes
  useEffect(() => {
    formatDataRef.current = formatData;
  }, [formatData]);

  useEffect(() => {
    setSearch("");
    setSearchQuery("");
  }, [isResetSearch]);

  useEffect(() => {
    setPage(0);
  }, [activeTab, statusFilter, searchQuery]);

  // Fetch initial counts for both tabs
  useEffect(() => {
    const fetchInitialCounts = async () => {
      if (!fieldId || countsLoaded) return;

      try {
        // Fetch active cycles count (PREPARE + ONGOING)
        const activeStatuses = ["PREPARE", "ONGOING"];
        let activeCount = 0;

        for (const status of activeStatuses) {
          const params = {
            filter_field_id: fieldId,
            filter_crop_status: status,
            page_number: 1,
            page_size: 1, // Just get count, not data
          };
          const res = await getApi("/v1/crop-cycle/list", params);
          if (res.status && res.data) {
            activeCount += res.data.totalRecords || 0;
          }
        }
        setActiveTotal(activeCount);

        // Fetch past cycles count (HARVESTED)
        const pastParams = {
          filter_field_id: fieldId,
          filter_crop_status: "HARVESTED",
          page_number: 1,
          page_size: 1, // Just get count, not data
        };
        const pastRes = await getApi("/v1/crop-cycle/list", pastParams);
        if (pastRes.status && pastRes.data) {
          setPastTotal(pastRes.data.totalRecords || 0);
        }

        setCountsLoaded(true);
      } catch (err) {
        console.error("Error fetching initial counts:", err);
      }
    };

    fetchInitialCounts();
  }, [fieldId, countsLoaded]);

  useEffect(() => {
    const fetchCycles = async () => {
      setLoading(true);
      setError(null);
      try {
        let cycles: any[] = [];
        let totalCount = 0;
        if (!fieldId) {
          setData([]);
          setLoading(false);
          return;
        }
        if (activeTab === "ACTIVE") {
          if (statusFilter && statusFilter.value) {
            // Only fetch the filtered status
            const params: any = {
              filter_field_id: fieldId,
              filter_crop_status: statusFilter.value,
              search: searchQuery || undefined,
              page_number: page + 1,
              page_size: rowsPerPage,
            };
            const res = await getApi("/v1/crop-cycle/list", params);
            if (res.status && res.data && Array.isArray(res.data.listItems)) {
              cycles = res.data.listItems;
              totalCount = res.data.totalRecords || 0;
            }
          } else {
            // Active tab with no status filter = PREPARE + ONGOING combined.
            // The crop-cycle API filters by a single status, so we fetch ALL
            // of each status (looping through its pages), merge + dedupe, then
            // paginate client-side. Paginating two separate server queries and
            // merging them can't represent a combined page correctly — that
            // previously capped the grid at the first 10 rows and reported the
            // wrong total (e.g. "1–10 of 10" for 20 active cycles).
            const statuses = ["PREPARE", "ONGOING"];
            const FETCH_PAGE_SIZE = 100;
            let merged: any[] = [];
            for (const status of statuses) {
              let pageNo = 1;
              let statusTotal = Number.POSITIVE_INFINITY;
              const collected: any[] = [];
              while (collected.length < statusTotal) {
                const params: any = {
                  filter_field_id: fieldId,
                  filter_crop_status: status,
                  search: searchQuery || undefined,
                  page_number: pageNo,
                  page_size: FETCH_PAGE_SIZE,
                };
                const res = await getApi("/v1/crop-cycle/list", params);
                if (
                  res.status &&
                  res.data &&
                  Array.isArray(res.data.listItems)
                ) {
                  collected.push(...res.data.listItems);
                  statusTotal = res.data.totalRecords ?? collected.length;
                  if (res.data.listItems.length === 0) break;
                  pageNo += 1;
                } else {
                  break;
                }
              }
              merged = merged.concat(collected);
            }
            // Remove duplicates by id
            const seen = new Set();
            const allActive = merged.filter((item) => {
              if (seen.has(item.id)) return false;
              seen.add(item.id);
              return true;
            });
            // True total drives the pagination; slice the current page locally.
            totalCount = allActive.length;
            cycles = allActive.slice(
              page * rowsPerPage,
              page * rowsPerPage + rowsPerPage,
            );
          }
          setData(formatData(cycles));
          setTotalRows(totalCount);
        } else {
          // PAST: HARVESTED only
          const params: any = {
            filter_field_id: fieldId,
            filter_crop_status: "HARVESTED",
            search: searchQuery || undefined,
            page_number: page + 1,
            page_size: pageSize,
          };
          const res = await getApi("/v1/crop-cycle/list", params);
          if (res.status && res.data && Array.isArray(res.data.listItems)) {
            cycles = res.data.listItems;
            totalCount = res.data.totalRecords || 0;
          }
          setData(formatData(cycles));
          setTotalRows(totalCount);
        }
      } catch (err) {
        setError(t("commonMessages.internalServerError"));
      } finally {
        setLoading(false);
      }
    };
    fetchCycles();
  }, [activeTab, fieldId, searchQuery, statusFilter, page, pageSize]);

  // Handle page change event.
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  // Search handlers
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };
  const handleSearch = () => {
    setSearch(search.trim());
    setSearchQuery(search.trim());
  };
  const handleClearSearch = () => {
    setSearch("");
    setSearchQuery("");
  };
  // Filter modal logic for ACTIVE tab
  const handleAddFilters = () => setIsFilterModalOpen(true);
  const handleCancelFilter = () => setIsFilterModalOpen(false);

  const handleDeleteFilter = () => {
    setStatusFilter(null);
    setFilterInitialValues({});
  };

  return (
    <>
      <Box
        sx={{
          backgroundColor: "#FFF",
          borderRadius: "8px",
          padding: "5px 10px",
          marginTop: 1,
          marginBottom: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Tabs
          value={activeTab === "ACTIVE" ? 0 : 1}
          onChange={(e, val) => setActiveTab(val === 0 ? "ACTIVE" : "PAST")}
          sx={{
            "& .MuiTabs-flexContainer": { justifyContent: "left" },
            "& .MuiTabs-indicator": { display: "none" },
            minHeight: 40,
          }}
        >
          <Tab
            label={
              <Button
                sx={{
                  backgroundColor:
                    activeTab === "ACTIVE" ? "farm.main" : "transparent",
                  color: activeTab === "ACTIVE" ? "#fff" : "farm.main",
                  px: 3,
                  py: 1,
                  fontWeight: 600,
                  fontSize: 14,
                  "&:hover": {
                    backgroundColor:
                      activeTab === "ACTIVE" ? "farm.main" : "transparent",
                    color: activeTab === "ACTIVE" ? "#fff" : "farm.main",
                  },
                  position: "relative",
                }}
              >
                {t("fieldDetails.activeCycles")}
                <Box
                  sx={{
                    ml: 1,
                    borderRadius: "15px",
                    backgroundColor:
                      activeTab === "ACTIVE" ? "#fff" : "farm.main",
                    color: activeTab === "ACTIVE" ? "farm.main" : "#fff",
                    fontWeight: 700,
                    fontSize: 14,
                    minWidth: 25,
                    textAlign: "center",
                    display: "inline-block",
                  }}
                >
                  {activeTotal}
                </Box>
              </Button>
            }
            sx={{ padding: 0, minWidth: "auto", marginRight: 1 }}
          />
          <Tab
            label={
              <Button
                sx={{
                  backgroundColor:
                    activeTab === "PAST" ? "farm.main" : "transparent",
                  color: activeTab === "PAST" ? "#fff" : "farm.main",
                  px: 3,
                  py: 1,
                  fontWeight: 600,
                  fontSize: 14,
                  "&:hover": {
                    backgroundColor:
                      activeTab === "PAST" ? "farm.main" : "transparent",
                    color: activeTab === "PAST" ? "#fff" : "farm.main",
                  },
                  position: "relative",
                }}
              >
                {t("fieldDetails.pastCycles")}
                <Box
                  sx={{
                    ml: 1,
                    borderRadius: "15px",
                    backgroundColor:
                      activeTab === "PAST" ? "#fff" : "farm.main",
                    color: activeTab === "PAST" ? "farm.main" : "#fff",
                    fontWeight: 700,
                    fontSize: 14,
                    minWidth: 25,
                    textAlign: "center",
                    display: "inline-block",
                  }}
                >
                  {pastTotal}
                </Box>
              </Button>
            }
            sx={{ padding: 0, minWidth: "auto" }}
          />
        </Tabs>

        {/* Search + Filter live in the tabs/toolbar row (matches the Crop
            Cycle Details layout) */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box display={"flex"} gap={2}>
            <FormControl
              variant="outlined"
              size="small"
              sx={{ flexGrow: 1, minWidth: 220 }}
            >
              <InputLabel htmlFor="outlined-adornment-search">
                {t("commonTable.search")}
              </InputLabel>
              <OutlinedInput
                id="outlined-adornment-search"
                value={search}
                onChange={handleSearchChange}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      onClick={handleClearSearch}
                      sx={{
                        visibility: search.length > 0 ? "visible" : "hidden",
                      }}
                    >
                      <i className="icon-close" />
                    </IconButton>
                  </InputAdornment>
                }
                label="Search"
              />
            </FormControl>
            <Button variant="contained" size="small" onClick={handleSearch}>
              {t("commonTable.search")}
            </Button>
          </Box>
          <Box display={"flex"}>
            {isFilterBtn && activeTab === "ACTIVE" && (
              <Button
                variant="outlined"
                sx={{
                  marginLeft: 2,
                  color: "risinblack.main",
                  borderColor: "risinblack.main",
                  height: 37,
                  minWidth: 100,
                }}
                startIcon={<i className="icon-filter"></i>}
                onClick={handleAddFilters}
              >
                {t("commonTable.filter")}
              </Button>
            )}
            <Box display={isHideImportExportAddBtns ? "none" : ""}>
              {buttonConfig &&
                buttonConfig.label &&
                buttonConfig.label !== "" && (
                  <Button
                    variant="contained"
                    onClick={buttonConfig.onClick}
                    startIcon={<i className="icon-addnew"></i>}
                    sx={{ color: "farm.contrastText", marginLeft: 2 }}
                  >
                    {buttonConfig.label}
                  </Button>
                )}
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <Paper
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            p: { xs: 1, sm: 2 },
            m: isHideImportExportAddBtns ? "20px auto" : "",
            minHeight: 0,
          }}
        >
          {statusFilter && statusFilter.value && (
            <Chip
              label={statusFilter.label}
              variant="outlined"
              onDelete={handleDeleteFilter}
              sx={{
                width: "fit-content",
                maxWidth: "100%",
                alignSelf: "flex-start",
              }}
            />
          )}
          {loading || !countsLoaded ? (
            <CircularProgress />
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <>
              <TableContainer
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  overflowX: "auto",
                  minHeight: "100px",
                  maxHeight: "100%",
                }}
                className="tablestyle"
              >
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      {columns.map((column, index) => (
                        <TableCell key={index}>
                          {t(`${tableName}.${column}`)}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={columns.length} align="center">
                          {t("commonMessages.noRecordsFound")}
                        </TableCell>
                      </TableRow>
                    ) : (
                      data.map((row, rowIndex) => (
                        <TableRow
                          key={rowIndex}
                          onClick={() =>
                            navigate(`/cropcycledetails?cropcycleid=${row.id}`)
                          }
                          sx={{ cursor: rowClick ? "pointer" : "default" }}
                        >
                          {columns.map((column, colIndex) => (
                            <TableCell key={colIndex}>
                              {row[column] ?? "-"}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={totalRows}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(event, newPage) => handlePageChange(newPage)}
                rowsPerPageOptions={[]}
              />
            </>
          )}
        </Paper>
        <CommonDrawer
          open={isFilterModalOpen}
          handleClose={handleCancelFilter}
          drawerWidth={450}
        >
          <Container>
            <Box mt={2} mb={2} className="container">
              <Typography>{t("commonFilter.addFilter")}</Typography>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setStatusFilter({
                    value: filterInitialValues.status || "",
                    label: filterInitialValues.statusLabel || "",
                  });
                  setIsFilterModalOpen(false);
                }}
              >
                <FormControl fullWidth sx={{ mt: 2, mb: 1 }}>
                  <Autocomplete
                    fullWidth
                    id="status-select"
                    options={[
                      {
                        label: t("commonStatus.cropStatus.prepare"),
                        value: "PREPARE",
                      },
                      {
                        label: t("commonStatus.cropStatus.ongoing"),
                        value: "ONGOING",
                      },
                    ]}
                    value={
                      [
                        {
                          label: t("commonStatus.cropStatus.prepare"),
                          value: "PREPARE",
                        },
                        {
                          label: t("commonStatus.cropStatus.ongoing"),
                          value: "ONGOING",
                        },
                      ].find(
                        (option) => option.value === filterInitialValues.status,
                      ) || null
                    }
                    onChange={(e, newValue) => {
                      setFilterInitialValues({
                        status: newValue?.value || "",
                        statusLabel: newValue?.label || "",
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="filled"
                        label={t("commonFilter.filterFields.cropCycleStatus")}
                        sx={{
                          "& .MuiFilledInput-root": {
                            height: "48px",
                          },
                        }}
                      />
                    )}
                    sx={{
                      "& .MuiAutocomplete-inputRoot": {
                        padding: "6 !important",
                        alignItems: "center",
                        justifyContent: "center",
                      },
                    }}
                  />
                </FormControl>

                <Box
                  mt={2}
                  className="button-container"
                  display="flex"
                  justifyContent="flex-end"
                  gap={2}
                >
                  <Button
                    variant="contained"
                    className="cancel-button"
                    onClick={handleCancelFilter}
                    sx={{
                      backgroundColor: "agricolors.whitebg",
                      color: "black",
                      border: 1,
                      borderColor: "risinblack.main",
                      "&:hover": {
                        backgroundColor: "#f0f0f0",
                      },
                      boxShadow: "none",
                    }}
                  >
                    {t("commonFilter.cancel")}
                  </Button>
                  <Button
                    variant="contained"
                    className="add-user-button"
                    type="submit"
                  >
                    {t("commonFilter.filter")}
                  </Button>
                </Box>
              </form>
            </Box>
          </Container>
        </CommonDrawer>
      </Box>
    </>
  );
};

export default CommonTable;
